import React from 'react'
import { CardColors } from '@memebattle/ligretto-shared'
import { ButtonBase } from '@mui/material'
import { Typography } from '@memebattle/ui'
import { styled } from '@mui/material/styles'
import type { CardPlaceSize } from '../CardPlace'
import { CardBackFace } from './CardBackFace'

type CardSize = 'small' | 'medium' | 'large'

interface CardProps {
  [dataAttribute: `data-${string}`]: string | boolean | undefined
  /** Color of card **/
  color?: CardColors
  /** Value of card **/
  value?: number
  /** Selected state of card **/
  isSelected?: boolean
  /** Disabled state of card **/
  isDisabled?: boolean
  /** Darkened state of card (E.x. when other card selected) **/
  isDarkened?: boolean
  /** Highlighted state of card **/
  isHighlighted?: boolean
  /** The card lies face down — the uniform back is drawn instead of the face **/
  isHidden?: boolean
  /** The card keeps its place in the layout but is not drawn (it is being dragged or placed) **/
  isInvisible?: boolean
  onClick?: () => void
  /** Size of card **/
  size?: CardSize
  ref?: React.Ref<HTMLButtonElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>
  onPointerDown?: React.PointerEventHandler<HTMLButtonElement>
  onTouchStart?: React.TouchEventHandler<HTMLButtonElement>
}

export const widthByCardSize: Record<CardSize, string> = {
  small: '3.875rem',
  medium: '6.125rem',
  large: '7.5rem',
}

export const heightByCardSize: Record<CardSize, string> = {
  small: '5.5rem',
  medium: '8.5rem',
  large: '10.75rem',
}

export const tabletHeightBySize: Record<CardPlaceSize, string> = {
  small: '4.375rem',
  medium: '5.875rem',
  large: '8rem',
}

export const tabletWidthBySize: Record<CardPlaceSize, string> = {
  small: '3.125rem',
  medium: '4.25rem',
  large: '5.75rem',
}

export const mobileHeightBySize: Record<CardPlaceSize, string> = {
  small: '3.125rem',
  medium: '4.125rem',
  large: '4.75rem',
}

export const mobileWidthBySize: Record<CardPlaceSize, string> = {
  small: '2.25rem',
  medium: '2.875rem',
  large: '3.5rem',
}

const fontSizeByCardSize: Record<CardSize, string> = {
  small: '3rem',
  medium: '6rem',
  large: '8rem',
}

const tabletFontSizeByCardSize: Record<CardSize, string> = {
  small: '2.5rem',
  medium: '4rem',
  large: '5rem',
}

const mobileFontSizeByCardSize: Record<CardSize, string> = {
  small: '2rem',
  medium: '3rem',
  large: '4rem',
}

const colorByCardColors: Record<CardColors, string> = {
  [CardColors.green]: '#2FB166',
  [CardColors.red]: '#F12F2F',
  [CardColors.yellow]: '#F5CF0E',
  [CardColors.blue]: '#29A7DD',
  [CardColors.empty]: 'transparent',
}

const StyledCardNotForwardedPropsSet = new Set<PropertyKey>([
  'isDarkened',
  'isDisabled',
  'isHidden',
  'isHighlighted',
  'isInvisible',
  'isSelected',
  'size',
])

const StyledCard = styled(ButtonBase, { shouldForwardProp: prop => !StyledCardNotForwardedPropsSet.has(prop) })<{
  color: CardColors
  isDisabled?: boolean
  isSelected?: boolean
  isDarkened?: boolean
  isHidden?: boolean
  isHighlighted?: boolean
  isInvisible?: boolean
  size: CardSize
}>(({ color, isDisabled, isSelected, isDarkened, isHidden, isHighlighted, isInvisible, size, theme }) => ({
  height: heightByCardSize[size],
  width: widthByCardSize[size],
  // A face-down card is visible regardless of its (unknown to the viewer) color.
  opacity: isInvisible || (color === CardColors.empty && !isHidden) ? 0 : 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  userSelect: 'none',
  // No face color behind the back artwork: its transparent rounded corners would let the color shine through.
  background: isHidden ? 'none' : color ? colorByCardColors[color] : colorByCardColors.empty,
  color: '#fff',
  fontSize: fontSizeByCardSize[size],
  filter: isDarkened ? 'grayscale(50%) brightness(0.95)' : 'none',
  cursor: isDisabled ? 'default' : 'pointer',
  // The board never scrolls, so the browser has no claim on a touch that starts on a card: dragging
  // reaches the sensor instead of being cancelled as a pan, and fast tapping does not zoom.
  touchAction: 'none',
  transition: 'box-shadow 100ms',
  ':hover': {
    boxShadow: isDisabled ? undefined : `0.1rem 0.1rem 0.8rem ${isSelected || isHighlighted ? 'rgba(255,255,255,0.7)' : 'rgba(0, 0, 0, 0.5)'} `,
  },
  boxShadow: isSelected || isHighlighted ? '0.1rem 0.1rem 0.8rem rgba(255,255,255,0.7)' : undefined,
  [theme.breakpoints.down('lg')]: {
    height: tabletHeightBySize[size],
    width: tabletWidthBySize[size],
    fontSize: tabletFontSizeByCardSize[size],
  },
  [theme.breakpoints.down('sm')]: {
    height: mobileHeightBySize[size],
    width: mobileWidthBySize[size],
    fontSize: mobileFontSizeByCardSize[size],
  },
  borderRadius: '0.25rem',
}))

export const Card: React.FC<CardProps> = ({
  value,
  isDisabled = false,
  isSelected,
  isDarkened,
  isHidden,
  isHighlighted,
  isInvisible,
  onClick,
  onMouseDown,

  color = CardColors.empty,
  size = 'medium',
  ...rest
}) => (
  <StyledCard
    {...rest}
    isDarkened={isDarkened}
    isHighlighted={isHighlighted}
    disableRipple={isDisabled}
    size={size}
    // Ligretto is a speed game: a card reacts on press, not on release. The drag sensor listens on
    // the same event, so both handlers run instead of one replacing the other.
    onMouseDown={event => {
      onMouseDown?.(event)
      onClick?.()
    }}
    isDisabled={isDisabled}
    isSelected={isSelected}
    isHidden={isHidden}
    isInvisible={isInvisible}
    color={color}
  >
    {isHidden ? (
      <CardBackFace />
    ) : (
      <Typography fontSize="inherit" component="span">
        {value}
      </Typography>
    )}
  </StyledCard>
)

Card.displayName = 'Card'
